import type { FieldValues, Path, PathValue } from 'react-hook-form';
import type { InspectorDTO } from 'src/types/dto';
import type { AddressInfoType } from 'src/types/inspector';
import type { MapProviderType } from 'src/types/map-provider';

import { APIProvider, InfoWindow, Map } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AutocompleteCustomHybrid } from './autocomplete-field';
import { InspectorInfoWindow } from './components/inspector-info-window';
import { useEnvMapProvider } from 'src/hooks/use-env-map-provider';
import type { LocationMapProps as OriginalLocationMapProps, Marker as OriginalMarker } from './types';

const isValidLatLng = (position: { lat: number; lng: number } | undefined): boolean =>
  position != null &&
  Number.isFinite(position.lat) &&
  Number.isFinite(position.lng);

interface ExtendedMarker extends OriginalMarker {
  id?: number;
}

interface ExtendedLocationMapProps<T extends FieldValues> extends OriginalLocationMapProps<T> {
  onChange?: (value: string) => void;
  highlightedMarkerId?: number;
  selectedIds?: number[];
  selectedMarker?: InspectorDTO | null;
  onMarkerSelect?: (inspector: InspectorDTO | null) => void;
  hideSearchInput?: boolean;
  defaultZoom?: number;
  markers?: ExtendedMarker[];
}

/**
 * Google Maps implementation with full search functionality
 */
export function UniversalLocationMapGoogle<T extends FieldValues>({
  methods,
  setAddressInfo,
  tooltipText,
  placeholderText,
  onChange,
  viewOnly = false,
  defaultLocation,
  markers = [],
  onMarkerDragEnd,
  onMarkerClick,
  highlightedMarkerId,
  selectedIds,
  selectedMarker,
  onMarkerSelect,
  onInspectorSelect,
  hideSearchInput = false,
  defaultZoom,
}: ExtendedLocationMapProps<T>) {
  const envMapProvider = useEnvMapProvider();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const markerRefs = useRef<(google.maps.Marker | null)[]>([]);
  const [currentAddress, setCurrentAddress] = useState<string>(
    methods?.getValues('address' as Path<T>) || ''
  );

  const [localMarkers, setLocalMarkers] = useState<ExtendedMarker[]>(() => {
    if (markers.length > 0) {
      return markers;
    }
    const location = methods?.getValues('location' as Path<T>) || defaultLocation;
    if (location) {
      const [lat, lng] = location.split(',').map(Number);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        return [
          {
            position: { lat, lng },
            draggable: !viewOnly,
            title: viewOnly ? 'Location' : 'Drag to update location',
          },
        ];
      }
    }
    return [
      {
        position: { lat: 52.520008, lng: 13.404954 },
        draggable: !viewOnly,
        title: viewOnly ? 'Location' : 'Drag to update location',
      },
    ];
  });

  const initialLocation = (() => {
    const location = methods?.getValues('location' as Path<T>) || defaultLocation;
    if (location) {
      const [lat, lng] = location.split(',').map(Number);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        return { lat, lng };
      }
    }

    const address = methods?.getValues('address' as Path<T>);
    const city = methods?.getValues('city' as Path<T>);
    const postalCode = methods?.getValues('postalCode' as Path<T>) || methods?.getValues('zipCode' as Path<T>);
    const country = methods?.getValues('country' as Path<T>);

    if (address || (city && postalCode) || (postalCode && country)) {
      return null;
    }

    return { lat: 52.520008, lng: 13.404954 };
  })();

  const [, setLocalAddressInfo] = useState<AddressInfoType>({
    country: methods?.getValues('country' as Path<T>) || '',
    city: methods?.getValues('city' as Path<T>) || '',
    postalCode: methods?.getValues('postalCode' as Path<T>) || '',
    region: methods?.getValues('region' as Path<T>) || '',
    zipCode: methods?.getValues('zipCode' as Path<T>) || '',
  });

  const getApiKey = () => {
    return process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  };

  const updateAddressFromLocation = useCallback(
    async (position: google.maps.LatLngLiteral) => {
      if (!geocoder) {
        throw new Error('Geocoder not available');
      }

      try {
        const response = await geocoder.geocode({ location: position });

        if (response.results[0]) {
          const place = response.results[0];
          const formattedAddress = place.formatted_address || '';

          methods?.setValue(
            'location' as Path<T>,
            `${position.lat},${position.lng}` as PathValue<T, Path<T>>,
            { shouldValidate: true, shouldDirty: true, shouldTouch: true }
          );

          methods?.setValue('address' as Path<T>, formattedAddress as PathValue<T, Path<T>>, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });

          setCurrentAddress(formattedAddress);

          const addressComponents = place.address_components || [];
          const country =
            addressComponents.find((component) => component.types.includes('country'))?.long_name ||
            '';
          const city =
            addressComponents.find((component) => component.types.includes('locality'))
              ?.long_name || '';
          const postalCode =
            addressComponents.find((component) => component.types.includes('postal_code'))
              ?.long_name || '';
          const region =
            addressComponents.find((component) =>
              component.types.includes('administrative_area_level_1')
            )?.long_name || '';

          const newAddressInfo = { country, city, postalCode, region, zipCode: postalCode };
          setAddressInfo?.(newAddressInfo);

          methods?.trigger(['location', 'address'] as Path<T>[]);

          return true;
        }
        console.error('No results from geocoder');
        throw new Error('No results from geocoder');
      } catch (error) {
        console.error('Error getting address from location:', error);
        throw error;
      }
    },
    [geocoder, methods, setAddressInfo, setCurrentAddress]
  );

  const createMarker = useCallback(
    (marker: ExtendedMarker, index: number) => {
      if (!map || !window.google) {
        console.error('Map or Google Maps not available for marker creation');
        return;
      }

      try {
        const isInspectorMarker = index > 0;
        const isSelected =
          isInspectorMarker &&
          marker &&
          'id' in marker &&
          selectedIds?.includes(marker.id as number);

        const createHouseIcon = (color: string) => {
          const iconUrl = `https://api.iconify.design/material-symbols:home-pin-rounded.svg?color=%23${color}&width=32&height=32`;
          return {
            url: iconUrl,
          };
        };

        const newMarker = new window.google.maps.Marker({
          map,
          position: marker.position,
          draggable: marker.draggable ?? !viewOnly,
          title: marker.title || (viewOnly ? 'Location' : 'Drag to update location'),
          animation: undefined,
          icon: isInspectorMarker ? createHouseIcon(isSelected ? '000000' : '4285F4') : marker.icon,
        });

        if (marker.onClick) {
          newMarker.addListener('click', () => {
            marker.onClick?.();
            onMarkerClick?.(index);
          });
        }

        if (newMarker.getDraggable()) {
          newMarker.addListener('dragend', async () => {
            const pos = newMarker.getPosition();
            if (pos) {
              const newPosition = { lat: pos.lat(), lng: pos.lng() };
              try {
                setLocalMarkers((prevMarkers) => {
                  const updatedMarkers = [...prevMarkers];
                  updatedMarkers[index] = {
                    ...updatedMarkers[index],
                    position: newPosition,
                  };
                  return updatedMarkers;
                });
                await updateAddressFromLocation(newPosition);
                onMarkerDragEnd?.(index, newPosition);
              } catch (error) {
                console.error('Error updating address from location:', error);
              }
            }
          });
        }

        markerRefs.current[index] = newMarker;
        newMarker.setMap(map);
      } catch (error) {
        console.error('Error creating marker:', error);
      }
    },
    [map, viewOnly, onMarkerDragEnd, onMarkerClick, updateAddressFromLocation, selectedIds]
  );

  useEffect(() => {
    if (!map || geocoder || typeof window === 'undefined' || !window.google?.maps?.importLibrary) {
      return;
    }

    let isMounted = true;

    window.google.maps
      .importLibrary('geocoding')
      .then((geocodingLibrary) => {
        if (!isMounted) return;

        setGeocoder(new (geocodingLibrary as google.maps.GeocodingLibrary).Geocoder());
      })
      .catch((error) => {
        console.error('Error initializing geocoder:', error);
      });

    return () => {
      isMounted = false;
    };
  }, [geocoder, map]);

  useEffect(() => {
    if (markers.length > 0) {
      setLocalMarkers(markers);
    }
  }, [markers]);

  useEffect(() => {
    if (!map || !geocoder) return;

    if (markers && markers.length > 0) {
      setLocalMarkers(markers);

      markers.forEach((marker, index) => {
        createMarker(marker, index);
      });

      const mappableMarkers = markers.filter((marker) => isValidLatLng(marker.position));

      if (mappableMarkers.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        mappableMarkers.forEach((marker) => bounds.extend(marker.position));
        map.fitBounds(bounds);
      } else if (mappableMarkers.length === 1) {
        map.setCenter(mappableMarkers[0].position);
        if (defaultZoom) map.setZoom(defaultZoom);
      }
    } else if (localMarkers.length > 0) {
      localMarkers.forEach((marker, index) => {
        createMarker(marker, index);
      });

      if (localMarkers.length === 1) {
        if (localMarkers[0]) {
          const hasSelectedInspectors = localMarkers
            .slice(1)
            .some((marker) => marker && 'id' in marker && selectedIds?.includes(marker.id as number));

          if (hasSelectedInspectors) {
            const bounds = new window.google.maps.LatLngBounds();
            let hasMappableBounds = false;
            const siteMarker = localMarkers[0];
            if (siteMarker && isValidLatLng(siteMarker.position)) {
              bounds.extend(siteMarker.position);
              hasMappableBounds = true;
            }

            localMarkers.slice(1).forEach((marker) => {
              if (
                marker &&
                'id' in marker &&
                selectedIds?.includes(marker.id as number) &&
                isValidLatLng(marker.position)
              ) {
                bounds.extend(marker.position);
                hasMappableBounds = true;
              }
            });

            if (hasMappableBounds) {
              const ne = bounds.getNorthEast();
              const sw = bounds.getSouthWest();
              const latPadding = (ne.lat() - sw.lat()) * 0.1;
              const lngPadding = (ne.lng() - sw.lng()) * 0.1;

              bounds.extend({ lat: ne.lat() + latPadding, lng: ne.lng() + lngPadding });
              bounds.extend({ lat: sw.lat() - latPadding, lng: sw.lng() - lngPadding });

              map.fitBounds(bounds);
            }
          } else if (localMarkers[0] && isValidLatLng(localMarkers[0].position)) {
            map.setCenter(localMarkers[0].position);
            map.setZoom(15);
          }
        }
      } else {
        const tryGeocodeExistingAddress = async () => {
          const address = methods?.getValues('address' as Path<T>);
          const city = methods?.getValues('city' as Path<T>);
          const postalCode = methods?.getValues('postalCode' as Path<T>) || methods?.getValues('zipCode' as Path<T>);
          const country = methods?.getValues('country' as Path<T>);

          let searchAddress = '';
          if (address) {
            searchAddress = address;
          } else if (city && postalCode) {
            searchAddress = `${city} ${postalCode}`;
            if (country) searchAddress += `, ${country}`;
          } else if (postalCode && country) {
            searchAddress = `${postalCode}, ${country}`;
          }

          if (searchAddress) {
            try {
              const response = await geocoder.geocode({ address: searchAddress });
              if (response.results[0]) {
                const location = response.results[0].geometry.location;
                const position = { lat: location.lat(), lng: location.lng() };

                const newMarker: ExtendedMarker = {
                  position,
                  draggable: !viewOnly,
                  title: viewOnly ? 'Location' : 'Drag to update location',
                };

                setLocalMarkers([newMarker]);
                map.setCenter(position);
                map.setZoom(15);

                methods?.setValue(
                  'location' as Path<T>,
                  `${position.lat},${position.lng}` as PathValue<T, Path<T>>,
                  { shouldValidate: true, shouldDirty: true, shouldTouch: true }
                );

                return;
              }
            } catch (error) {
              console.error('Error geocoding existing address:', error);
            }
          }

          if (initialLocation) {
            const defaultPosition = initialLocation;
            map.setCenter(defaultPosition);
            map.setZoom(15);
          }
        };

        if (!initialLocation) {
          tryGeocodeExistingAddress();
        } else {
          const defaultPosition = initialLocation;
          map.setCenter(defaultPosition);
          map.setZoom(15);
        }
      }
    }
  }, [map, geocoder, localMarkers, createMarker, initialLocation, viewOnly, selectedIds, markers, methods]);

  useEffect(() => {
    if (!map) return;

    markerRefs.current.forEach((marker, index) => {
      if (index >= localMarkers.length) {
        marker?.setMap(null);
        markerRefs.current[index] = null;
      }
    });

    localMarkers.forEach((marker, index) => {
      const existingMarker = markerRefs.current[index];
      if (existingMarker) {
        existingMarker.setPosition(marker.position);
        existingMarker.setDraggable(marker.draggable ?? !viewOnly);
        existingMarker.setTitle(
          marker.title || (viewOnly ? 'Location' : 'Drag to update location')
        );
      } else {
        createMarker(marker, index);
      }
    });
  }, [localMarkers, map, createMarker, viewOnly]);

  useEffect(() => {
    if (!map || !window.google) return;

    markerRefs.current.forEach((marker, index) => {
      if (marker) {
        const markerData = localMarkers[index];
        if (markerData && 'id' in markerData && markerData.id === highlightedMarkerId) {
          marker.setAnimation(window.google.maps.Animation.BOUNCE);
        } else {
          marker.setAnimation(null);
        }
      }
    });
  }, [highlightedMarkerId, map, localMarkers]);

  useEffect(() => {
    if (!map || !window.google) return;

    markerRefs.current.forEach((marker, index) => {
      if (marker) {
        const markerData = localMarkers[index];

        if (index > 0 && markerData && 'id' in markerData) {
          const isSelected = selectedIds?.includes(markerData.id as number);
          const createHouseIcon = (color: string) => {
            const iconUrl = `https://api.iconify.design/material-symbols:home-pin-rounded.svg?color=%23${color}&width=32&height=32`;
            return {
              url: iconUrl,
            };
          };
          marker.setIcon(createHouseIcon(isSelected ? '000000' : '4285F4'));
        }
      }
    });
  }, [selectedIds, map, localMarkers]);

  useEffect(() => {
    const formAddress = methods?.getValues('address' as Path<T>);
    if (formAddress && formAddress !== currentAddress) {
      setCurrentAddress(formAddress);
    }
  }, [methods]);

  const updateAddressInfo = (addressComponents: google.maps.GeocoderAddressComponent[]) => {
    const country =
      addressComponents.find((component) => component.types.includes('country'))?.long_name || '';
    const city =
      addressComponents.find((component) => component.types.includes('locality'))?.long_name || '';
    const postalCode =
      addressComponents.find((component) => component.types.includes('postal_code'))?.long_name ||
      '';
    const region =
      addressComponents.find((component) => component.types.includes('administrative_area_level_1'))
        ?.long_name || '';

    const newAddressInfo = { country, city, postalCode, region, zipCode: postalCode };
    setLocalAddressInfo(newAddressInfo);
    setAddressInfo?.(newAddressInfo);

    methods?.setValue('country' as Path<T>, country as PathValue<T, Path<T>>, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    methods?.setValue('city' as Path<T>, city as PathValue<T, Path<T>>, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    methods?.setValue('postalCode' as Path<T>, postalCode as PathValue<T, Path<T>>, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    methods?.setValue('region' as Path<T>, region as PathValue<T, Path<T>>, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handlePlaceSelect = async (place: google.maps.places.PlaceResult | null) => {
    if (!place || !place.geometry?.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const newPosition = { lat, lng };

    if (map) {
      const newMarker: ExtendedMarker = {
        position: newPosition,
        draggable: !viewOnly,
        title: viewOnly ? 'Location' : 'Drag to update location',
      };

      setLocalMarkers([newMarker]);

      map.panTo(newPosition);
      map.setZoom(17);

      const formattedAddress = place.formatted_address || '';
      setCurrentAddress(formattedAddress);

      methods?.setValue(
        'location' as Path<T>,
        `${newPosition.lat},${newPosition.lng}` as PathValue<T, Path<T>>,
        { shouldValidate: true, shouldDirty: true, shouldTouch: true }
      );

      methods?.setValue('address' as Path<T>, formattedAddress as PathValue<T, Path<T>>, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });

      if (place.address_components) {
        updateAddressInfo(place.address_components);
      }
    }
  };

  return (
    <APIProvider
      apiKey={getApiKey() || ''}
      libraries={['places', 'geocoding']}
    >
      <div
        style={{
          position: 'relative',
          height: '300px',
          width: '100%',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Map
          defaultCenter={initialLocation || { lat: 52.520008, lng: 13.404954 }}
          defaultZoom={defaultZoom ?? (viewOnly ? 15 : 17)}
          disableDefaultUI
          fullscreenControl={false}
          streetViewControl={false}
          mapTypeControl={false}
          scaleControl={false}
          zoomControl
          gestureHandling="cooperative"
          styles={[
            {
              featureType: 'poi',
              stylers: [{ visibility: 'off' }],
            },
            {
              featureType: 'poi.park',
              stylers: [{ visibility: 'off' }],
            },
            {
              featureType: 'poi.business',
              stylers: [{ visibility: 'off' }],
            },
            {
              featureType: 'poi.attraction',
              stylers: [{ visibility: 'off' }],
            },
            {
              featureType: 'poi.place_of_worship',
              stylers: [{ visibility: 'off' }],
            },
            {
              featureType: 'poi.sports_complex',
              stylers: [{ visibility: 'off' }],
            },
            {
              featureType: 'transit',
              stylers: [{ visibility: 'off' }],
            },
          ]}
          onIdle={(event) => {
            if (!map && event.map) {
              setMap(event.map);
            }
          }}
        >
          {!viewOnly && !hideSearchInput && (
            <AutocompleteCustomHybrid
              tooltipText={tooltipText}
              placeholderText={placeholderText}
              defaultPlace={methods?.getValues('address' as Path<T>)}
              value={currentAddress}
              onChange={(newValue: string) => {
                setCurrentAddress(newValue);
                methods?.setValue('address' as Path<T>, newValue as PathValue<T, Path<T>>, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
              onPlaceSelect={(place: google.maps.places.PlaceResult | null) => {
                handlePlaceSelect(place);
              }}
            />
          )}
          {selectedMarker && selectedMarker.location && (
            <InfoWindow
              position={{
                lat: Number(selectedMarker.location.split(',')[0]),
                lng: Number(selectedMarker.location.split(',')[1]),
              }}
              onCloseClick={() => onMarkerSelect?.(null)}
            >
              <InspectorInfoWindow
                inspector={selectedMarker}
                isSelected={selectedIds?.includes(selectedMarker.id) ?? false}
                onSelect={() => {
                  onInspectorSelect?.(selectedMarker.id);
                  onMarkerSelect?.(null);
                }}
              />
            </InfoWindow>
          )}
        </Map>
      </div>
    </APIProvider>
  );
}

export default UniversalLocationMapGoogle;
